// Compact "Add to calendar" chip that renders next to the prominent lab date.
// Generates both a Google Calendar URL and a downloadable .ics file so parents
// on Apple/Outlook can add the event just as easily.
// No external deps — pulls date/duration/pricing straight from site_content.
import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ChevronDown, ExternalLink, Download } from "lucide-react";
import { useSiteContent } from "@/lib/siteContent";

// Format a JS Date to the UTC "YYYYMMDDTHHMMSSZ" shape both ics + gcal want.
function toIcsUtc(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

export default function AddToCalendar() {
  const sc = useSiteContent();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Bail silently if the ISO date isn't set — Countdown already handles this
  // gracefully, and there's nothing useful to add.
  if (!sc.course_date_iso) return null;

  const start = new Date(sc.course_date_iso);
  const durationHrs = parseFloat(sc.course_duration_hours || "2.5");
  const end = new Date(start.getTime() + durationHrs * 60 * 60 * 1000);

  const title = "AI Build Lab — Live";
  const details =
    "Live 2.5-hour AI Build Lab with Donja Williams. Learn to use AI and no-code tools to build websites, apps, dashboards, and workflows. " +
    "Zoom link will be emailed after registration. https://donjawilliams.com";
  const location = "Live online (Zoom)";

  // --- Google Calendar URL ---
  const gcalUrl = new URL("https://calendar.google.com/calendar/render");
  gcalUrl.searchParams.set("action", "TEMPLATE");
  gcalUrl.searchParams.set("text", title);
  gcalUrl.searchParams.set("details", details);
  gcalUrl.searchParams.set("location", location);
  gcalUrl.searchParams.set("dates", `${toIcsUtc(start)}/${toIcsUtc(end)}`);

  // --- Outlook.com Calendar URL ---
  const outlookUrl = new URL("https://outlook.live.com/calendar/0/deeplink/compose");
  outlookUrl.searchParams.set("path", "/calendar/action/compose");
  outlookUrl.searchParams.set("rru", "addevent");
  outlookUrl.searchParams.set("subject", title);
  outlookUrl.searchParams.set("body", details);
  outlookUrl.searchParams.set("location", location);
  outlookUrl.searchParams.set("startdt", start.toISOString());
  outlookUrl.searchParams.set("enddt", end.toISOString());

  // --- Apple / generic .ics blob ---
  const downloadIcs = () => {
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Freedom In Me//AI Build Lab//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${start.getTime()}@donjawilliams.com`,
      `DTSTAMP:${toIcsUtc(new Date())}`,
      `DTSTART:${toIcsUtc(start)}`,
      `DTEND:${toIcsUtc(end)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${details.replace(/\n/g, "\\n")}`,
      `LOCATION:${location}`,
      "URL:https://donjawilliams.com",
      "STATUS:CONFIRMED",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ai-build-lab.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={ref} data-testid="add-to-calendar">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="add-to-calendar-trigger"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 h-8 pl-2.5 pr-2 rounded-full bg-brand-soft border border-brand/20 text-brand text-[12px] font-semibold hover:bg-brand/10 transition-colors"
      >
        <CalendarPlus size={13} />
        Add to calendar
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-2 left-0 sm:left-auto sm:right-0 min-w-[220px] rounded-xl bg-white border border-[var(--line)] shadow-xl overflow-hidden"
          data-testid="add-to-calendar-menu"
        >
          <a
            href={gcalUrl.toString()}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            data-testid="atc-google"
            className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px] text-ink hover:bg-bone transition-colors"
          >
            <span className="font-medium">Google Calendar</span>
            <ExternalLink size={12} className="text-ink/40" />
          </a>
          <a
            href={outlookUrl.toString()}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            data-testid="atc-outlook"
            className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px] text-ink hover:bg-bone transition-colors border-t border-[var(--line)]"
          >
            <span className="font-medium">Outlook.com</span>
            <ExternalLink size={12} className="text-ink/40" />
          </a>
          <button
            type="button"
            onClick={downloadIcs}
            data-testid="atc-ics"
            className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-[13px] text-ink hover:bg-bone transition-colors border-t border-[var(--line)] text-left"
          >
            <span className="font-medium">Apple / Outlook (.ics)</span>
            <Download size={12} className="text-ink/40" />
          </button>
        </div>
      )}
    </div>
  );
}
